import { StageMode } from '../types';

export interface LiveWsCallbacks {
  onReady?: (sessionId: string) => void;
  onServerAudio?: (pcmBase64: string) => void;
  onSpokenCue?: (spokenText: string) => void;
  onTurnComplete?: () => void;
  onInterrupted?: () => void;
  onStageUpdate?: (mode: StageMode, data: any) => void;
  onSessionUpdate?: (sessionId: string, deviceId: string) => void;
  onError?: (err: string) => void;
  onClose?: () => void;
}

const MAX_PENDING_OUTBOUND_MESSAGES = 120;

export class LiveWsClient {
  private ws: WebSocket | null = null;
  private isExplicitlyClosed = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private callbacks: LiveWsCallbacks = {};
  private pendingOutboundQueue: string[] = [];

  private currentDeviceId = '';
  private currentSessionId = '';

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private flushPendingOutbound(socket: WebSocket): void {
    while (this.pendingOutboundQueue.length > 0 && socket.readyState === WebSocket.OPEN) {
      const serialized = this.pendingOutboundQueue.shift()!;
      socket.send(serialized);
    }
  }

  private enqueueOrSend(payload: Record<string, any>): void {
    if (this.isExplicitlyClosed) return;
    if (!this.ws && this.reconnectTimer === null) return;
    const serialized = JSON.stringify(payload);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.flushPendingOutbound(this.ws);
      this.ws.send(serialized);
    } else if (
      (this.ws && this.ws.readyState === WebSocket.CONNECTING) ||
      this.reconnectTimer !== null
    ) {
      if (this.pendingOutboundQueue.length < MAX_PENDING_OUTBOUND_MESSAGES) {
        this.pendingOutboundQueue.push(serialized);
      }
    }
  }

  private detachAndCloseSocket(socket: WebSocket | null): void {
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      try {
        socket.close();
      } catch {}
    }
  }

  private scheduleReconnect(): boolean {
    if (this.isExplicitlyClosed || this.reconnectAttempts >= this.maxReconnectAttempts) {
      return false;
    }
    this.reconnectAttempts++;
    const delay = Math.min(250 * Math.pow(2, this.reconnectAttempts - 1), 3000);
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isExplicitlyClosed) {
        this.openSocket(true);
      }
    }, delay);
    return true;
  }

  public connect(deviceId: string, sessionId?: string, callbacks: LiveWsCallbacks = {}): void {
    this.clearReconnectTimer();
    if (this.ws) {
      this.detachAndCloseSocket(this.ws);
      this.ws = null;
    }

    this.callbacks = callbacks;
    this.currentDeviceId = deviceId;
    this.currentSessionId = sessionId || '';
    this.isExplicitlyClosed = false;
    this.reconnectAttempts = 0;
    this.pendingOutboundQueue.length = 0;

    this.openSocket(false);
  }

  private openSocket(isRetry: boolean): void {
    this.clearReconnectTimer();
    if (this.ws) {
      this.detachAndCloseSocket(this.ws);
      this.ws = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const query = new URLSearchParams();
    query.set('deviceId', this.currentDeviceId);
    if (this.currentSessionId) {
      query.set('sessionId', this.currentSessionId);
    }
    if (isRetry) {
      query.set('_retry', String(this.reconnectAttempts));
      query.set('_t', String(Date.now()));
    }

    const wsUrl = `${protocol}//${host}/api/live/ws?${query.toString()}`;

    try {
      const socket = new WebSocket(wsUrl);
      this.ws = socket;
      let hadErrorEvent = false;
      let hadServerError = false;
      let reportedError = false;

      socket.onopen = () => {
        if (this.ws !== socket) return;
        this.flushPendingOutbound(socket);
      };

      socket.onmessage = (event) => {
        if (this.ws !== socket) return;
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'ready') {
            this.reconnectAttempts = 0;
            if (this.callbacks.onReady) {
              this.callbacks.onReady(msg.sessionId);
            }
          } else if (msg.type === 'serverContent') {
            if (msg.interrupted && this.callbacks.onInterrupted) {
              this.callbacks.onInterrupted();
            }
            if (msg.audio && this.callbacks.onServerAudio) {
              this.callbacks.onServerAudio(msg.audio);
            }
            if (msg.spokenText && this.callbacks.onSpokenCue) {
              this.callbacks.onSpokenCue(msg.spokenText);
            }
          } else if (msg.type === 'turnComplete') {
            if (this.callbacks.onTurnComplete) {
              this.callbacks.onTurnComplete();
            }
          } else if (msg.type === 'stageUpdate') {
            if (this.callbacks.onStageUpdate) {
              this.callbacks.onStageUpdate(msg.mode as StageMode, msg.data);
            }
          } else if (msg.type === 'session_update') {
            this.currentSessionId = msg.sessionId;
            if (this.callbacks.onSessionUpdate) {
              this.callbacks.onSessionUpdate(msg.sessionId, msg.deviceId);
            }
          } else if (msg.type === 'error') {
            hadServerError = true;
            reportedError = true;
            if (this.callbacks.onError) {
              this.callbacks.onError(msg.message || 'Live session error');
            }
          }
        } catch (e) {
          console.error('[LiveWS] Failed to parse message:', e);
        }
      };

      socket.onerror = () => {
        if (this.ws !== socket) return;
        hadErrorEvent = true;
        if (this.isExplicitlyClosed) return;
        if (this.reconnectAttempts >= this.maxReconnectAttempts && !reportedError) {
          reportedError = true;
          if (this.callbacks.onError) {
            this.callbacks.onError('Live WebSocket connection error');
          }
        }
      };

      socket.onclose = (event?: CloseEvent) => {
        if (this.ws !== socket) return;
        this.ws = null;

        if (this.isExplicitlyClosed) {
          return;
        }

        const isNormalClose = event?.code === 1000;
        if (!isNormalClose && !hadServerError && this.scheduleReconnect()) {
          return;
        }

        this.pendingOutboundQueue.length = 0;
        if (hadErrorEvent && !reportedError && this.callbacks.onError) {
          reportedError = true;
          this.callbacks.onError('Live WebSocket connection error');
        }
        if (this.callbacks.onClose) {
          this.callbacks.onClose();
        }
      };
    } catch (e: any) {
      if (!this.scheduleReconnect() && this.callbacks.onError) {
        this.callbacks.onError(e?.message || 'Failed to initialize WebSocket');
      }
    }
  }

  public sendAudioChunk(pcmBase64: string): void {
    this.enqueueOrSend({
      type: 'realtimeInput',
      audio: pcmBase64
    });
  }

  public sendAudioEnd(): void {
    this.enqueueOrSend({
      type: 'audioStreamEnd',
      end: true
    });
  }

  public sendClientContent(action: string, data?: any, turnComplete?: boolean): void {
    const isSilentUiNavAction = [
      'back_to_spotlight',
      'back_to_showcase',
      'back_to_calculator',
      'filter_corridor',
      'clear_voice_filter',
      'sync_screen_context',
      'swipe_showcase_photo',
      'switch_showcase_focus',
      'filter_photos',
      'adjust_guests',
      'select_slot',
      'select_package',
      'change_date'
    ].includes(action);
    this.enqueueOrSend({
      type: 'sendClientContent',
      role: 'user',
      parts: [{ text: `UI_ACTION: ${JSON.stringify({ action, ...data })}` }],
      action,
      data,
      turnComplete: turnComplete !== undefined ? turnComplete : !isSilentUiNavAction
    });
  }

  public close(): void {
    this.isExplicitlyClosed = true;
    this.reconnectAttempts = 0;
    this.pendingOutboundQueue.length = 0;
    this.clearReconnectTimer();
    if (this.ws) {
      const socket = this.ws;
      this.ws = null;
      this.detachAndCloseSocket(socket);
    }
  }

  public isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

export const liveWsClient = new LiveWsClient();
